import React from 'react';
import {
  CardProps as ChakraCardProps,
  Card as ChakraCard,
  forwardRef,
} from '@chakra-ui/react';
import {
  ColorMode,
  useColorMode,
} from '@allergan-data-labs/alle-elements-core';
import { getCardStyles } from './card.theme';

export interface CardProps extends Omit<ChakraCardProps, 'colorScheme'> {
  /**
   * Default theme color
   * @default light
   */
  colorMode?: ColorMode;

  /**
   * If `true`, Card will render with a border
   * @default true
   */
  withBorder?: boolean;
}

/**
 * Card is a flexible component used to group and display content in a clear and concise format.
 */
export const Card = forwardRef<CardProps, 'div'>((props, ref) => {
  const {
    colorMode: propColorMode,
    children,
    withBorder = true,
    onClick,
    ...rest
  } = props;
  const { colorMode } = useColorMode(propColorMode);
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const keyCode = event.code !== 'Unknown' ? event.code : event.key;
    if (keyCode === 'Enter' || keyCode === 'Space') {
      if (event.target instanceof HTMLElement) {
        event.target.click();
      }
    }
  };
  return (
    <ChakraCard
      ref={ref}
      tabIndex={onClick && 0}
      onClick={onClick}
      onKeyDown={onKeyDown}
      colorScheme={colorMode}
      border={withBorder ? undefined : 'none'}
      sx={getCardStyles(colorMode)}
      {...rest}
    >
      {children}
    </ChakraCard>
  );
});

export { CardHeader, CardBody, CardFooter } from '@chakra-ui/react';
